/**
 * Residents (cutover remediation T2/T3, 2026-09-20): the door a resident
 * enters eMerge through without Monday, and the resolver's open doubts.
 *
 * The screen asks the resolver before it creates: "is this person known?"
 * The answer is one word and, when there are candidates, the candidates with
 * what matched and what differed — never a number. A row is created on
 * CREATE_NEW, or on the advocate's explicit, reasoned call when the resolver
 * says AMBIGUOUS. IDENTITY_CONFLICT is not the advocate's call: it goes to a
 * manager's identity review. The SSN travels once and is cleared.
 */
import React, { useEffect, useState } from 'react';
import { advocateApi, freshKey, shortDate } from './advocateApi';
import type { Scope } from './advocateApi';
import {
  residentsApi, DECISION_LABEL, KIND_LABEL, signalWord,
  Candidate, CreateOutcome, IdentityScope, Resolution, Review, Signals,
} from './residentsApi';

const errText = (r: { error?: string; message?: string; code?: string; status?: number; retryAfterSeconds?: number }): string => {
  // 2026-10-05: a real 429 says nothing was changed and when to try again; the form keeps what was typed.
  if (r.status === 429) {
    const min = Math.max(1, Math.ceil((r.retryAfterSeconds && r.retryAfterSeconds > 0 ? r.retryAfterSeconds : 900) / 60));
    return `Nothing was changed. Too many requests from your account in the last few minutes. Wait about ${min} minute${min === 1 ? '' : 's'}, then try again. Your entry is still here.`;
  }
  return r.message || r.error || (r.code ? `The server refused this (${r.code}).` : 'The server refused this.');
};

/** T3 (charter §33): an empty queue is not truth when its source failed. Shown only when the state changes what to do. */
export function SourceHealthNotice({ scope }: { scope: Scope }) {
  const h = scope.source_health;
  if (!h || !h.notice) return null;
  return (
    <div className={`pa-note pa-fresh${h.notice.tone === 'critical' ? ' stop' : ''}`} role={h.notice.tone === 'critical' ? 'alert' : 'status'}>
      <span><strong>CounselEAR: {h.state.toLowerCase()}.</strong> {h.notice.text}</span>
    </div>
  );
}

function SignalList({ s }: { s: Signals | null }) {
  if (!s) return null;
  const rows: [string, keyof Signals][] = [['SSN', 'ssn'], ['CounselEAR id', 'counselear_id'], ['Date of birth', 'dob'], ['Last name', 'last_name'], ['First name', 'first_name']];
  return (
    <ul className="pa-signals" aria-label="What matched">
      {rows.filter(([, k]) => s[k] !== null).map(([label, k]) => <li key={k} className={s[k] === true ? 'ok' : 'stop'}>{label} {signalWord(s[k])}</li>)}
    </ul>
  );
}

function CandidateCard({ c, action }: { c: Candidate; action?: React.ReactNode }) {
  const name = [c.first_name, c.last_name].filter(Boolean).join(' ') || 'Name not recorded';
  return (
    <li className="pa-candidate">
      <div>
        <strong>{name}</strong>
        <span className="pa-text none"> · DOB {c.dob ? shortDate(c.dob) : 'not recorded'} · CounselEAR {c.counselear_id || 'none'} · SSN {c.ssn_masked || 'none on file'}</span>
        <span className="pa-text none"> · {c.facility_name || 'building not recorded'} · {c.source_system === 'EMERGE' ? 'created in eMerge' : 'from the board'}</span>
      </div>
      <SignalList s={c.signals} />
      {action}
    </li>
  );
}

export function NewResidentForm({ onCreated }: { onCreated: (patientId: string, msg: string) => void }) {
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [dob, setDob] = useState('');
  const [ceId, setCeId] = useState('');
  const [ssn, setSsn] = useState('');
  const [reason, setReason] = useState('');
  const [fq, setFq] = useState('');
  const [hits, setHits] = useState<{ id: string; name: string; city: string | null; state: string | null }[]>([]);
  const [facility, setFacility] = useState<{ id: string; name: string } | null>(null);
  const [resolution, setResolution] = useState<Resolution | null>(null);
  const [outcome, setOutcome] = useState<CreateOutcome | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [key, setKey] = useState(freshKey());

  useEffect(() => {
    if (fq.trim().length < 2) { setHits([]); return; }
    let live = true;
    (async () => { const r = await advocateApi.facilities(fq.trim()); if (live && r.success && r.data) setHits(r.data.items); })();
    return () => { live = false; };
  }, [fq]);

  const facts = () => ({ first_name: first.trim(), last_name: last.trim(), dob, counselear_id: ceId.trim() || undefined, ssn: ssn.replace(/\D/g, '') ? ssn : undefined });
  const ready = first.trim() && last.trim() && /^\d{4}-\d{2}-\d{2}$/.test(dob);
  const clearSecret = () => setSsn('');

  const resolve = async () => {
    if (!ready) { setErr('First name, last name and date of birth are required.'); return; }
    if (ceId.trim() && !/^\d{1,12}$/.test(ceId.trim())) {
      setErr('CounselEAR id must contain 1 to 12 digits.'); setResolution(null); setOutcome(null); return;
    }
    setBusy(true); setErr(null); setOutcome(null);
    try { const r = await residentsApi.resolve(facts()); if (!r.success || !r.data) { setErr(errText(r)); setResolution(null); return; } setResolution(r.data); }
    catch { setErr('Could not reach the server.'); } finally { setBusy(false); }
  };

  const create = async (override?: 'CREATE_ANYWAY' | 'USE_CANDIDATE', candidateId?: string) => {
    setBusy(true); setErr(null);
    try {
      const r = await residentsApi.create({ ...facts(), facility_id: facility?.id, reason: reason.trim(), override: override || null, override_candidate_id: candidateId || null, idempotency_key: key });
      clearSecret();
      if (!r.success || !r.data) { setErr(errText(r)); return; }
      setOutcome(r.data);
      if (r.data.patient_id && (r.data.created || r.data.decision === 'MATCH_EXISTING')) {
        onCreated(r.data.patient_id, r.data.created ? 'Resident created in eMerge. CounselEAR tags and the intake rule pick them up on the next half-hour.' : 'That resident already exists; you have been pointed at their record.');
        setKey(freshKey());
      }
    } catch { clearSecret(); setErr('Could not reach the server. Nothing was created.'); } finally { setBusy(false); }
  };

  const d = outcome?.decision || resolution?.decision || null;
  const candidates = outcome?.candidates || resolution?.candidates || [];

  return (
    <section className="pa-card" aria-label="New resident">
      <div className="pa-card-head"><h2>New resident</h2><span className="why">For a person met at a building or found in CounselEAR who is not in eMerge yet.</span></div>
      <form className="pa-form" autoComplete="off" onSubmit={(e) => { e.preventDefault(); resolve(); }}>
        <div className="pa-grid2">
          <label><span>First name</span><input id="resident-first-name" name="first_name" autoComplete="given-name" value={first} onChange={(e) => { setFirst(e.target.value); setResolution(null); }} maxLength={120} aria-label="First name" /></label>
          <label><span>Last name</span><input id="resident-last-name" name="last_name" autoComplete="family-name" value={last} onChange={(e) => { setLast(e.target.value); setResolution(null); }} maxLength={120} aria-label="Last name" /></label>
          <label><span>Date of birth</span><input id="resident-dob" name="dob" autoComplete="bday" type="date" value={dob} onChange={(e) => { setDob(e.target.value); setResolution(null); }} aria-label="Date of birth" /></label>
          <label><span>CounselEAR id (if known)</span><input id="resident-counselear-id" name="counselear_id" autoComplete="off" value={ceId} inputMode="numeric" onChange={(e) => { setCeId(e.target.value); setResolution(null); }} maxLength={12} aria-label="CounselEAR id" /></label>
          <label><span>SSN (if known)</span><input id="resident-ssn" name="ssn" type="password" inputMode="numeric" value={ssn} onChange={(e) => { setSsn(e.target.value); setResolution(null); }} maxLength={11} aria-label="SSN" autoComplete="off" /></label>
          <label><span>Building</span><input value={facility ? facility.name : fq} onChange={(e) => { setFacility(null); setFq(e.target.value); }} placeholder="Type the building's name" aria-label="Search buildings" /></label>
        </div>
        {!facility && hits.length > 0 && (
          <ul className="pa-hits" aria-label="Buildings">{hits.map((h) => <li key={h.id}><button type="button" className="pa-link" onClick={() => { setFacility({ id: h.id, name: h.name }); setHits([]); }}>{h.name}{h.city ? ` · ${h.city}${h.state ? `, ${h.state}` : ''}` : ''}</button></li>)}</ul>
        )}
        <label><span>How the person came to be known</span><input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} aria-label="Reason" /></label>
        <p className="pa-text none">The SSN is encrypted on entry and never shown here. eMerge checks first whether this person already exists.</p>
        <div className="btns"><button type="submit" className="pa-open pa-primary" disabled={busy || !ready}>{busy ? 'Checking…' : 'Check if already known'}</button></div>
        {err && <p className="pa-notice stop" role="alert">{err}</p>}
      </form>

      {d && (
        <div className="pa-section" aria-label="Identity decision">
          <h3>{DECISION_LABEL[d]}</h3>
          {candidates.length > 0 && (
            <ul className="pa-candidates" aria-label="Candidates">
              {candidates.map((c) => (
                <CandidateCard key={c.id} c={c} action={
                  d === 'AMBIGUOUS' && !outcome?.patient_id ? (
                    <button type="button" className="pa-open" disabled={busy || !reason.trim()} onClick={() => create('USE_CANDIDATE', c.id)}>This is the same person</button>
                  ) : d === 'MATCH_EXISTING' ? (
                    <button type="button" className="pa-open" onClick={() => onCreated(c.id, 'That resident already exists; you have been pointed at their record.')}>Open this resident</button>
                  ) : null
                } />
              ))}
            </ul>
          )}
          {d === 'CREATE_NEW' && !outcome?.created && (
            <div className="btns"><button type="button" className="pa-open pa-primary" disabled={busy || !reason.trim()} onClick={() => create()}>{busy ? 'Creating…' : 'Create the resident'}</button></div>
          )}
          {d === 'AMBIGUOUS' && !outcome?.patient_id && (
            <div className="btns">
              <button type="button" className="pa-open" disabled={busy || !reason.trim()} onClick={() => create('CREATE_ANYWAY')}>Different person — create anyway</button>
              {!reason.trim() && <span className="pa-text none">Say why in the reason field first.</span>}
            </div>
          )}
          {d === 'IDENTITY_CONFLICT' && <p className="pa-notice stop" role="alert">Strong evidence points at an existing resident and disagrees with what was entered. A PA manager resolves this in Identity review before anyone creates a row.</p>}
          {outcome?.created && <p className="pa-notice" role="status">Created.</p>}
        </div>
      )}
    </section>
  );
}

export function IdentityReviews() {
  const [items, setItems] = useState<Review[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const load = async () => {
    try { const r = await residentsApi.reviews('OPEN'); if (r.success && r.data) { setItems(r.data.items); setErr(null); } else setErr(errText(r)); }
    catch { setErr('Could not reach the server.'); }
  };
  useEffect(() => { load(); }, []);
  const resolve = async (id: string, resolution: 'RESOLVED_SAME_PERSON' | 'RESOLVED_DISTINCT' | 'WAIVED') => {
    setBusy(id);
    try { const r = await residentsApi.resolveReview(id, { resolution, note: (note[id] || '').trim() }); if (!r.success) { setErr(errText(r)); return; } await load(); }
    catch { setErr('Could not reach the server.'); } finally { setBusy(null); }
  };
  return (
    <section className="pa-card" aria-label="Identity review">
      <div className="pa-card-head"><h2>Identity review</h2><span className="why">Where the resolver would not decide alone: the same SSN with a different date of birth, one CounselEAR id on two rows, a name and date that match. Nothing is merged; your answer is recorded with your name.</span></div>
      {err && <p className="pa-notice stop" role="alert">{err}</p>}
      {items && items.length === 0 && <p className="pa-text none" style={{ padding: 14 }}>Nothing waiting.</p>}
      {items && items.map((r) => (
        <div key={r.id} className="pa-section">
          <h3>{KIND_LABEL[r.kind]} · {DECISION_LABEL[r.decision]} · seen {shortDate(r.created_at)}</h3>
          {r.reason && <p className="pa-text none">Entered reason: {r.reason}</p>}
          <ul className="pa-candidates">
            {r.subject && 'first_name' in r.subject && <CandidateCard c={{ ...(r.subject as Candidate), signals: null }} />}
            {r.candidates.map((c) => <CandidateCard key={c.id} c={c} />)}
          </ul>
          <label className="pa-form"><span>Your note</span><input value={note[r.id] || ''} onChange={(e) => setNote({ ...note, [r.id]: e.target.value })} maxLength={2000} aria-label="Resolution note" /></label>
          <div className="btns">
            <button type="button" className="pa-open" disabled={busy === r.id || !(note[r.id] || '').trim()} onClick={() => resolve(r.id, 'RESOLVED_SAME_PERSON')}>Same person</button>
            <button type="button" className="pa-open" disabled={busy === r.id || !(note[r.id] || '').trim()} onClick={() => resolve(r.id, 'RESOLVED_DISTINCT')}>Different people</button>
            <button type="button" className="pa-link" disabled={busy === r.id || !(note[r.id] || '').trim()} onClick={() => resolve(r.id, 'WAIVED')}>Waive</button>
          </div>
        </div>
      ))}
    </section>
  );
}

export default function ResidentsView({ identity, onCreated }: { identity: IdentityScope; onCreated: (patientId: string, msg: string) => void }) {
  return (
    <div className="pa-residents">
      {identity.can.create && <NewResidentForm onCreated={onCreated} />}
      {identity.held.create && !identity.can.create && <p className="pa-notice" role="status">Creating residents in eMerge is not open on this environment yet.</p>}
      {identity.held.review && <IdentityReviews />}
    </div>
  );
}
